import {
  CHECKOUT_MICRO_STEPS,
  PATIENT_UI_STEP_ORDER_EXISTING,
  PATIENT_UI_STEP_ORDER_GUEST,
  PATIENT_UI_STEP_ORDER_EXISTING_NO_WIDGET,
  PATIENT_UI_STEP_ORDER_GUEST_NO_WIDGET,
  formatReferrer,
  sourceHint,
  SOURCE_FILTER_OPTIONS
} from './trackingWords';
import { UK_TIME_ZONE } from './ukTime';

export { formatReferrer, sourceHint, SOURCE_FILTER_OPTIONS };
export {
  UK_TIME_ZONE,
  addCalendarDays,
  canShiftRangeForward,
  datePresets,
  defaultDateRange,
  largerDatePresets,
  rangeParams,
  shiftDateRange,
  spanDaysInclusive,
  todayDateRange,
  toInputDate,
  ukTodayYmd
} from './ukTime';

export const STEP_LABELS = {
  WIDGET: 'Widget',
  ENTRY: 'Checkout',
  DETAILS: 'Checkout',
  ASSESSMENT: 'Checkout',
  ACCOUNT: 'Checkout',
  PAYMENT: 'Checkout',
  BOOKING: 'Checkout',
  CONFIRMATION: 'Confirmation',
  PROFILE: 'Complete your profile',
  ADDITIONAL_DETAILS: 'Additional details',
  FINISHED: 'Finished all steps',
  CHECKOUT: 'Checkout',
  SUPPORT: 'Contacted support',
  OPERATIONS: 'System change'
};

export const STATUS_LABELS = {
  ACTIVE: 'Browsing now',
  IDLE: 'Paused',
  ABANDONED: 'Left without finishing',
  COMPLETED: 'Finished all steps'
};

export const EVENT_LABELS = {
  SESSION_STARTED: 'Started the booking journey',
  STEP_ENTERED: 'Moved to a new step',
  PAGE_VIEWED: 'Opened a step',
  STEP_COMPLETED: 'Finished a step',
  STEP_FAILED: 'Had a problem on a step',
  DETAILS_COMPLETED: 'Submitted personal details',
  DETAILS_FAILED: 'Details form had an error',
  ASSESSMENT_COMPLETED: 'Completed health questions',
  ASSESSMENT_FAILED: 'Health questions failed',
  OTP_REQUESTED: 'Asked for verification code',
  OTP_VERIFIED: 'Verified phone / email',
  OTP_FAILED: 'Verification code failed',
  REGISTERED: 'Created account',
  REGISTRATION_FAILED: 'Account creation failed',
  PAYMENT_STARTED: 'Started payment',
  PAYMENT_SUCCEEDED: 'Payment succeeded',
  PAYMENT_FAILED: 'Payment failed',
  BOOKING_SUBMITTED: 'Submitted booking',
  BOOKING_COMPLETED: 'Appointment booked',
  BOOKING_FAILED: 'Booking failed',
  PROFILE_COMPLETED: 'Finished profile / password',
  ADDITIONAL_DETAILS_COMPLETED: 'Submitted additional details',
  JOURNEY_COMPLETED: 'Finished all registration steps',
  FIELD_CLICKED: 'Clicked a form field',
  CTA_CLICKED: 'Clicked a button',
  SLOT_SELECTED: 'Selected an appointment time',
  SLOT_LIST_VIEWED: 'Viewed available times',
  SLOT_UNAVAILABLE: 'No suitable time available',
  EXIT_REASON_SELECTED: 'Shared why they left',
  API_CALL: 'Backend request'
};

export function prettyStep(step) {
  if (!step) return 'Unknown step';
  const code = String(step).toUpperCase();
  if (code === 'WIDGET') return 'Widget';
  if (CHECKOUT_MICRO_STEPS.includes(code) || code === 'CHECKOUT') return 'Checkout';
  return STEP_LABELS[code] || String(step).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function prettyStatus(status) {
  if (!status) return 'Unknown';
  return STATUS_LABELS[status] || String(status).replace(/_/g, ' ');
}

export function isBooked(row) {
  return !!(row && (row.orderId || row.appointmentId));
}

/** Sessions list: booked vs consult completed only (maps to API `booked=yes|completed`). */
export const BOOKING_FILTER_OPTIONS = [
  { value: 'yes', label: 'Booked' },
  { value: 'completed', label: 'Consult completed' }
];

export const DATE_BASIS_OPTIONS = [
  { value: '', label: 'Journey started', hint: 'Journeys that started in the date range' },
  { value: 'booked', label: 'Booked on', hint: 'Journeys whose order was placed in the date range' },
  { value: 'completed', label: 'Consult completed on', hint: 'Journeys whose consultation was completed in the date range (by appointment time)' }
];

/** Mirrors OnboardingStopReason.Code on the backend. */
export const STOP_REASON_OPTIONS = [
  { value: 'PAYMENT_DECLINED', label: 'Card declined by the bank' },
  { value: 'PAYMENT_3DS', label: 'Card security check (3D Secure) not completed' },
  { value: 'PAYMENT_FAILED', label: 'Payment did not go through' },
  { value: 'SLOT_TAKEN', label: 'Appointment time no longer available' },
  { value: 'OTP_FAILED', label: 'Verification code did not work' },
  { value: 'ACCOUNT_EXISTS', label: 'Email or phone already has an account' },
  { value: 'LOGIN_FAILED', label: 'Could not sign in' },
  { value: 'NOT_ELIGIBLE', label: 'Health questions not passed' },
  { value: 'FORM_REJECTED', label: 'Form details were rejected' },
  { value: 'REGISTRATION_FAILED', label: 'Could not create their account' },
  { value: 'BOOKING_FAILED', label: 'Booking could not be saved' },
  { value: 'SERVER_ERROR', label: 'Our system returned an error' },
  { value: 'CONNECTION_LOST', label: 'Connection dropped' },
  { value: 'OTHER_ERROR', label: 'Error on the page' },
  { value: 'LEFT_NO_ERROR', label: 'Left without an error' },
  { value: 'STILL_ON_SITE', label: 'Still on the site' }
];

const STOP_SIDE_LABELS = { OUR_SIDE: 'Our side', BANK: 'Bank', PATIENT: 'Patient', NONE: '' };

export function stopSideLabel(side) {
  return STOP_SIDE_LABELS[side] || '';
}

/** Red = fix on our side, amber = bank or patient, grey = no error logged. */
export function stopReasonClass(side) {
  if (side === 'OUR_SIDE') return 'bad';
  if (side === 'BANK' || side === 'PATIENT') return 'warn';
  return 'neutral';
}

export function prettyStopReason(code) {
  const opt = STOP_REASON_OPTIONS.find((o) => o.value === code);
  return (opt && opt.label) || (code ? String(code).replace(/_/g, ' ').toLowerCase() : '');
}

export function bookingOutcome(row) {
  if (row && row.bookingOutcome) return row.bookingOutcome;
  return isBooked(row) ? 'BOOKED' : 'NOT_BOOKED';
}

export function prettyBookingOutcome(row) {
  const outcome = bookingOutcome(row);
  if (outcome === 'COMPLETED') return 'Consult completed';
  if (outcome === 'BOOKED') return 'Booked';
  return 'Not booked';
}

export function bookingOutcomeClass(row) {
  const outcome = bookingOutcome(row);
  if (outcome === 'COMPLETED') return 'ok';
  if (outcome === 'BOOKED') return 'info';
  return 'neutral';
}

/**
 * Session status is the whole registration journey, not the appointment.
 * After payment the patient can still be on Confirmation / Profile / Additional details,
 * so ACTIVE + booked is "still on site after booking", not "hasn't booked".
 */
export function prettyJourneyStatus(row) {
  const status = row && row.status;
  if (isBooked(row)) {
    if (status === 'ACTIVE') return 'Finishing after booking';
    if (status === 'IDLE') return 'Paused after booking';
    if (status === 'ABANDONED') return 'Booked — extra steps not finished';
  }
  return prettyStatus(status);
}

export function journeyStatusTitle(row) {
  if (isBooked(row) && row.status === 'ACTIVE') {
    return 'Appointment is booked. They are still on Confirmation, profile or additional details.';
  }
  if (isBooked(row) && row.status === 'ABANDONED') {
    return 'Appointment is booked. They left before finishing profile / additional details.';
  }
  if (isBooked(row) && row.status === 'IDLE') {
    return 'Appointment is booked. Tab is open but they have gone quiet.';
  }
  return '';
}

export function prettyEvent(name) {
  if (!name) return 'Activity';
  return EVENT_LABELS[name] || String(name).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function prettyError(code) {
  const map = {
    CARD_DECLINED: 'Card was declined',
    PAYMENT_FAILED: 'Payment did not go through',
    OTP_INVALID: 'Wrong verification code',
    OTP_FAILED: 'Verification failed',
    NO_SLOTS: 'No appointment slots available',
    SLOT_LOAD_FAILED: 'Could not load appointment times',
    VALIDATION_FAILED: 'Form details were incomplete',
    ASSESSMENT_INVALID: 'Health questions incomplete',
    REGISTRATION_FAILED: 'Could not create account',
    UNKNOWN_ERROR: 'Unknown error',
    UNKNOWN_PATIENT: 'Unknown patient',
    UNKNOWN: 'Unknown error',
    UNSPECIFIED: 'Unspecified error'
  };
  if (!code) return 'Unknown problem';
  return map[code] || String(code).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

export function formatMoney(value) {
  return Number(value || 0).toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP'
  });
}

/**
 * e.g. 08/09/2026, 8:37:33 pm BST
 */
export function formatUkDateTime(value, { withSeconds = true } = {}) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const opts = {
    timeZone: UK_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  };
  if (withSeconds) opts.second = '2-digit';
  return new Intl.DateTimeFormat('en-GB', opts).format(d);
}

/**
 * e.g. 8:37:33 pm BST
 */
export function formatUkTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  }).format(d);
}

export function formatPct(value, digits = 1) {
  return `${Number(value || 0).toFixed(digits)}%`;
}

export function formatDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  return `${m}m ${String(r).padStart(2, '0')}s`;
}

export const TRACKING_NAV = [
  { label: 'Dashboard', to: '/tracking/overview', hint: 'Simple summary' },
  { label: 'Live now', to: '/tracking/live-journeys', hint: 'People booking right now' },
  { label: 'All journeys', to: '/tracking/sessions', hint: 'Search past booking attempts' },
  { label: 'Journey steps', to: '/tracking/funnels', hint: 'Where people drop off' },
  { label: 'Problems', to: '/tracking/errors', hint: 'HTTP 500 errors only' },
  { label: 'APIs', to: '/tracking/apis', hint: 'Backend call speed and failures' },
  { label: 'Busy times', to: '/tracking/traffic', hint: 'When people visit' },
  { label: 'Doctor slots', to: '/tracking/capacity', hint: 'Demand vs availability' },
  { label: 'Support', to: '/tracking/support', hint: 'Why patients ring us' },
  { label: 'Fixes', to: '/tracking/issues', hint: 'Track improvements' },
  { label: 'Weekly story', to: '/tracking/weekly-report', hint: 'Report for the business' },
  { label: 'GPS AI', to: '/tracking/gps-ai', hint: 'Claude-powered answers: connection, keys and features', ai: true }
];

/** Shared step filter options — same wording as the patient registration page. */
export const STEP_FILTER_OPTIONS = [
  { value: 'WIDGET', label: 'Widget' },
  { value: 'ENTRY', label: 'Checkout' },
  { value: 'DETAILS', label: 'Checkout' },
  { value: 'ASSESSMENT', label: 'Checkout' },
  { value: 'ACCOUNT', label: 'Checkout' },
  { value: 'PAYMENT', label: 'Checkout' },
  { value: 'BOOKING', label: 'Checkout' },
  { value: 'CONFIRMATION', label: 'Confirmation' },
  { value: 'PROFILE', label: 'Complete your profile' },
  { value: 'ADDITIONAL_DETAILS', label: 'Additional details' }
];

/** Deduped patient-facing step filter for admin dropdowns. */
export const PATIENT_STEP_FILTER_OPTIONS = [
  { value: 'WIDGET', label: 'Widget' },
  { value: 'CHECKOUT', label: 'Checkout' },
  { value: 'CONFIRMATION', label: 'Confirmation' },
  { value: 'PROFILE', label: 'Complete your profile' },
  { value: 'ADDITIONAL_DETAILS', label: 'Additional details' },
  { value: 'FINISHED', label: 'Finished all steps' }
];

export function prettyUserType(userType) {
  const key = String(userType || '').toUpperCase();
  if (key === 'EXISTING') return 'Signed in';
  if (key === 'GUEST') return 'New patient';
  return 'Unknown';
}

export function cameFromWidget(entrySurface, entryPage, events) {
  const surface = String(entrySurface || '').toUpperCase();
  // startsWith, not equality: covers WIDGET_COMPACT and any future WIDGET_* variant
  // without this list needing another edit each time.
  if (surface.startsWith('WIDGET') || surface === 'PAYMENT_FROM_WIDGET') return true;
  const page = String(entryPage || '').toLowerCase();
  if (page.includes('booking-widget')) return true;
  return (events || []).some((ev) => {
    const step = String(ev.step || '').toUpperCase();
    const evPage = String(ev.page || '').toLowerCase();
    return step === 'WIDGET' || evPage.includes('booking-widget');
  });
}

export function isWidgetPage(page) {
  return String(page || '').toLowerCase().includes('booking-widget');
}

export function isRegisterPage(page) {
  const p = String(page || '').toLowerCase();
  return p.includes('/register') || p.includes('/payment');
}

export function isWidgetEvent(ev) {
  if (!ev) return false;
  const step = String(ev.step || '').toUpperCase();
  if (step === 'WIDGET') return true;
  // Legacy widget events were tagged ENTRY but lived on the widget URL.
  if (isWidgetPage(ev.page)) return true;
  return false;
}

/** Checkout / confirmation / profile / details live on the register app, not the widget. */
export function isRegisterEvent(ev) {
  if (!ev) return false;
  if (isWidgetEvent(ev)) return false;
  const step = String(ev.step || '').toUpperCase();
  if (['DETAILS', 'ASSESSMENT', 'ACCOUNT', 'PAYMENT', 'BOOKING', 'CONFIRMATION', 'PROFILE', 'ADDITIONAL_DETAILS'].includes(step)) {
    return true;
  }
  if (isRegisterPage(ev.page)) return true;
  // ENTRY on register pages counts as checkout start; ENTRY on widget is widget.
  if (step === 'ENTRY' && !isWidgetPage(ev.page)) return true;
  return false;
}

export function toPatientUiStage(step, ev) {
  const code = String(step || '').toUpperCase();
  if (code === 'WIDGET' || isWidgetEvent(ev || { step })) return 'WIDGET';
  if (CHECKOUT_MICRO_STEPS.includes(code) || code === 'CHECKOUT') return 'CHECKOUT';
  if (code === 'CONFIRMATION') return 'CONFIRMATION';
  if (code === 'PROFILE') return 'PROFILE';
  if (code === 'ADDITIONAL_DETAILS' || code === 'FINISHED') return 'ADDITIONAL_DETAILS';
  return 'CHECKOUT';
}

export function patientUiStepOrder(userType, entrySurface, entryPage, events) {
  const existing = String(userType || '').toUpperCase() === 'EXISTING';
  const withWidget = cameFromWidget(entrySurface, entryPage, events);
  if (withWidget) {
    return existing ? PATIENT_UI_STEP_ORDER_EXISTING : PATIENT_UI_STEP_ORDER_GUEST;
  }
  return existing ? PATIENT_UI_STEP_ORDER_EXISTING_NO_WIDGET : PATIENT_UI_STEP_ORDER_GUEST_NO_WIDGET;
}

export { CHECKOUT_MICRO_STEPS, PATIENT_UI_STEP_ORDER_GUEST, PATIENT_UI_STEP_ORDER_EXISTING };

export function prettyCtaAction(action) {
  const key = String(action || '').trim();
  const map = {
    continue_to_payment: 'Book Consultation',
    book_consultation: 'Book Consultation',
    book_consultation_goodbody: 'Book Consultation',
    choose_date: 'Choose date',
    select_appointment_time: 'Select an appointment time',
    open_terms: 'Terms link',
    open_privacy_policy: 'Privacy policy link'
  };
  if (map[key]) return map[key];
  if (!key) return 'a button';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function prettyEntrySurface(surface) {
  const key = String(surface || '').toUpperCase();
  const map = {
    WIDGET: 'Booking widget',
    WIDGET_COMPACT: 'Compact widget',
    PAYMENT_DIRECT: 'Payment page (direct)',
    PAYMENT_FROM_WIDGET: 'Payment via widget',
    REGISTER: 'Registration page',
    LOGIN: 'Login page',
    REGISTER_TO_PAYMENT: 'Registration → payment',
    ADMIN_BOOKING: 'Admin booking (payment link)',
    UNKNOWN: 'Unknown'
  };
  return map[key] || (surface || 'Unknown');
}

/** Where the journey started — same values as the Entry column. */
export const ENTRY_SURFACE_FILTER_OPTIONS = [
  'WIDGET',
  'WIDGET_COMPACT',
  'PAYMENT_DIRECT',
  'PAYMENT_FROM_WIDGET',
  'REGISTER',
  'LOGIN',
  'REGISTER_TO_PAYMENT',
  'ADMIN_BOOKING',
  'UNKNOWN'
].map((value) => ({ value, label: prettyEntrySurface(value) }));

/** Map technical field names / noisy DOM labels → admin-readable names (keys are normalized). */
const FIELD_FRIENDLY_LABELS = {
  firstname: 'First name',
  first_name: 'First name',
  lastname: 'Last name',
  last_name: 'Last name',
  email: 'Email address',
  patientemail: 'Email address',
  telnumberfield: 'Phone number',
  phone: 'Phone number',
  day: 'Date of birth · day',
  month: 'Date of birth · month',
  year: 'Date of birth · year',
  patientgendermale: 'Gender · Male',
  patientgenderfemale: 'Gender · Female',
  gendermale: 'Gender · Male',
  genderfemale: 'Gender · Female',
  patientpostcodeinput: 'Postcode',
  bookerpostcodeinput: 'Booker postcode',
  line1: 'Address line 1',
  line2: 'Address line 2',
  city: 'Town / city',
  county: 'County',
  conditioninput: 'Appointment reason',
  conditionsearchinput: 'Appointment reason',
  bookerfirstname: 'Booker first name',
  bookerlastname: 'Booker last name',
  bookeremail: 'Booker email',
  bookertelnumberfield: 'Booker phone number',
  bookerday: 'Booker date of birth · day',
  bookermonth: 'Booker date of birth · month',
  bookeryear: 'Booker date of birth · year',
  bookergendermale: 'Booker gender · Male',
  bookergenderfemale: 'Booker gender · Female',
  bookerrelationship: 'Relationship to patient',
  copyguardian: 'Copy details from patient',
  discountcode: 'Discount code',
  otp: 'Verification code',
  accepted: 'Terms accepted',
  card: 'Card / payment details',
  payment: 'Payment details',
  paymentcard: 'Card / payment details',
  stripecard: 'Card / payment details',
  debitcreditcard: 'Payment method · Card',
  googlepay: 'Payment method · Google Pay',
  applepay: 'Payment method · Apple Pay',
  q4aanswer: 'Consultation type',
  audio: 'Consultation type · Audio',
  video: 'Consultation type · Video',
  customselecttrigger: 'Address lookup'
};

const PLACEHOLDER_TO_LABEL = {
  'enter first name': 'First name',
  'enter last name': 'Last name',
  'enter email address': 'Email address',
  'enter phone number': 'Phone number',
  'enter address line 1': 'Address line 1',
  'enter address line 2': 'Address line 2',
  'enter town': 'Town / city',
  'enter county': 'County',
  'select post code': 'Postcode',
  'select a reason': 'Appointment reason',
  'select relationship': 'Relationship to patient',
  'enter discount code': 'Discount code',
  'enter address': 'Address lookup',
  'enter address manually': 'Enter address manually'
};

function normalizeFieldKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function looksLikeNoiseLabel(label) {
  const raw = String(label || '').replace(/\s+/g, ' ').trim();
  if (!raw) return true;
  const lower = raw.toLowerCase();
  if (['input', 'select', 'button', 'textarea', 'div', 'span'].includes(lower)) return true;
  if (raw.length > 55) return true;
  // Concatenated appointment-type button copy, e.g. "Video consultationcallGreat for…"
  if (/consultationcall/i.test(raw) || /audiocall/i.test(raw)) return true;
  if (/great for discussions/i.test(raw) || /perfect if you prefer/i.test(raw)) return true;
  return false;
}

/**
 * Turn stored fieldName/fieldLabel into something the admin team can read.
 */
export function prettyFieldLabel(itemOrMeta = {}) {
  const meta = itemOrMeta.metadata || itemOrMeta.metadataJson || itemOrMeta;
  const parseMeta = typeof meta === 'string'
    ? (() => { try { return JSON.parse(meta); } catch { return {}; } })()
    : (meta || {});

  const fieldName = String(
    parseMeta.fieldName || parseMeta.FIELD_NAME || itemOrMeta.fieldName || ''
  ).trim();
  const fieldLabel = String(
    parseMeta.fieldLabel || parseMeta.FIELD_LABEL || itemOrMeta.fieldLabel || ''
  ).trim();
  const section = String(
    parseMeta.sectionName || parseMeta.SECTION_NAME || itemOrMeta.sectionName || ''
  ).toLowerCase();

  const nameKey = normalizeFieldKey(fieldName);
  const labelKey = normalizeFieldKey(fieldLabel);

  // Exact / normalized technical names first
  if (FIELD_FRIENDLY_LABELS[nameKey]) return FIELD_FRIENDLY_LABELS[nameKey];

  // Consultation type radios / buttons
  if (/^q4a_answer/i.test(fieldName) || /select-(audio|video)-consultation/i.test(fieldName)
    || /consultationcall|great for discussions|perfect if you prefer/i.test(fieldName + fieldLabel)) {
    if (/video/i.test(fieldName) || /video/i.test(fieldLabel)) return 'Consultation type · Video';
    if (/audio/i.test(fieldName) || /audio/i.test(fieldLabel)) return 'Consultation type · Audio';
    return 'Consultation type';
  }

  // Placeholder-style labels
  const placeholderHit = PLACEHOLDER_TO_LABEL[fieldLabel.toLowerCase()];
  if (placeholderHit) return placeholderHit;

  // Section fallbacks for anonymous inputs (e.g. Stripe / discount)
  if (looksLikeNoiseLabel(fieldLabel) || looksLikeNoiseLabel(fieldName)) {
    if (section.includes('payment') || section.includes('stripe') || section.includes('discount')) {
      if (section.includes('discount') || nameKey === 'input') {
        // Bare discount <input> often has fieldName "input"
        if (section.includes('discount') || /discount/i.test(fieldLabel)) return 'Discount code';
      }
      if (section.includes('payment') || section.includes('stripe')) return 'Card / payment details';
    }
    if (section.includes('consultation') || section.includes('appointment')) return 'Consultation type';
    if (nameKey === 'input' && /payment|card|stripe/i.test(String(itemOrMeta.page || ''))) {
      return 'Card / payment details';
    }
    if (nameKey === 'input') return 'Form field';
  }

  if (FIELD_FRIENDLY_LABELS[labelKey]) return FIELD_FRIENDLY_LABELS[labelKey];

  // Prefer a clean stored label when it is already readable
  if (fieldLabel && !looksLikeNoiseLabel(fieldLabel)) {
    return fieldLabel.replace(/\s+/g, ' ').trim().slice(0, 60);
  }
  if (fieldName && !looksLikeNoiseLabel(fieldName)) {
    return fieldName.replace(/([A-Z])/g, ' $1').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
  }
  return 'Form field';
}

export function isExpectedLookupMiss(apiPath, httpStatus, errorCode) {
  const path = String(apiPath || '').toLowerCase();
  const lookup = path.indexOf('find-patient-by-email') >= 0 || path.indexOf('chat-assessment/email') >= 0;
  if (!lookup) return false;
  if (Number(httpStatus) === 404) return true;
  const code = String(errorCode || '').toLowerCase();
  return code.indexOf('unknown patient') >= 0
    || code.indexOf('unknown user') >= 0
    || code.indexOf('no matching chat assessment') >= 0;
}

export function isServerErrorStatus(httpStatus) {
  const n = Number(httpStatus);
  return Number.isFinite(n) && n >= 500;
}

export function isTrackedApiFault(item = {}) {
  if (!isServerErrorStatus(item.httpStatus)) return false;
  return !isExpectedLookupMiss(item.apiPath, item.httpStatus, item.errorCode);
}

export function isApiActivity(eventName, item = {}) {
  return String(eventName || '').toUpperCase() === 'API_CALL' || !!(item && item.apiPath);
}

export function describeActivity(eventName, item = {}) {
  const name = String(eventName || '');
  const meta = item.metadata || item.metadataJson || {};
  const parseMeta = typeof meta === 'string' ? (() => { try { return JSON.parse(meta); } catch { return {}; } })() : meta;

  if (name === 'API_CALL' || item.apiPath) {
    const method = (parseMeta.httpMethod || parseMeta.HTTP_METHOD || item.httpMethod || 'CALL').toString().toUpperCase();
    const path = item.apiPath || parseMeta.apiPath || 'backend';
    const ms = item.durationMs != null ? ` · ${item.durationMs}ms` : '';
    const failed = isTrackedApiFault(item);
    const status = item.httpStatus != null ? ` · HTTP ${item.httpStatus}` : '';
    return {
      kind: 'api',
      kindLabel: failed ? 'Backend API 500' : 'Backend API',
      label: `${method} ${path}${status}${ms}`,
      hint: failed
        ? 'This backend request returned HTTP 500 while the patient was booking.'
        : 'This is a server/API request made while the patient was booking.'
    };
  }

  const uiEvents = {
    FIELD_CLICKED: 'Form field',
    CTA_CLICKED: 'Button click',
    SLOT_SELECTED: 'Chose a time',
    SLOT_LIST_VIEWED: 'Viewed times',
    EXIT_REASON_SELECTED: 'Exit reason'
  };
  if (uiEvents[name]) {
    let label = EVENT_LABELS[name] || uiEvents[name];
    if (name === 'FIELD_CLICKED') {
      label = `Tapped field: ${prettyFieldLabel({ ...item, metadata: parseMeta })}`;
    } else if (name === 'CTA_CLICKED') {
      const action = parseMeta.action || parseMeta.ACTION || parseMeta.actionLabel;
      const option = parseMeta.selectedOption || parseMeta.SELECTED_OPTION;
      label = `Clicked: ${prettyCtaAction(action)}`;
      if (option) label += ` (${option})`;
    } else if (name === 'SLOT_SELECTED') {
      const option = parseMeta.selectedOption || parseMeta.SELECTED_OPTION;
      label = option ? `Selected appointment: ${option}` : 'Selected an appointment time';
    }
    return {
      kind: 'ui',
      kindLabel: 'Patient action',
      label,
      hint: 'Something the patient clicked or typed on the page (not a backend call).'
    };
  }

  if (name === 'PAGE_VIEWED') {
    const stage = String(parseMeta.uiStage || parseMeta.UI_STAGE || toPatientUiStage(item.step, item) || '').toUpperCase();
    const stageLabel = STEP_LABELS[stage] || stage || 'a step';
    const reload = parseMeta.isReload === true || parseMeta.isReload === 'true' || parseMeta.visitReason === 'refresh';
    return {
      kind: 'journey',
      kindLabel: 'Journey step',
      label: reload ? `Refreshed ${stageLabel}` : `Opened ${stageLabel}`,
      hint: 'Patient opened or refreshed this step in the booking journey.'
    };
  }

  return {
    kind: 'journey',
    kindLabel: 'Journey step',
    label: EVENT_LABELS[name] || prettyEvent(name),
    hint: 'A booking journey milestone (started, moved step, paid, booked, etc.).'
  };
}

export function buildInsight(summary = {}) {
  const started = Number(summary.sessionsStarted || 0);
  const unique = Number(summary.uniqueVisitors || 0);
  const duplicates = Number(summary.duplicateSessions || 0);
  const booked = Number(summary.bookingsCompleted || 0);
  const abandoned = Number(summary.abandoned || 0);
  const rate = Number(summary.completionRate || 0);
  const topDrop = (summary.topDropOffs && summary.topDropOffs[0]) || null;
  const topError = (summary.topErrors && summary.topErrors[0]) || null;

  let tone = 'ok';
  if (rate < 15) tone = 'bad';
  else if (rate < 30) tone = 'warn';

  const lines = [];
  lines.push(`${formatNumber(started)} journeys started from ${formatNumber(unique)} unique visitors (${formatNumber(duplicates)} return/duplicate sessions).`);
  lines.push(`${formatNumber(booked)} finished booking (${formatPct(rate)}).`);
  if (abandoned > 0) {
    lines.push(`${formatNumber(abandoned)} left without booking.`);
  }
  const breakdown = summary.entryBreakdown || [];
  if (breakdown.length) {
    const parts = breakdown.slice(0, 3).map((row) =>
      `${prettyEntrySurface(row.surface)}: ${formatNumber(row.sessions)}`
    );
    lines.push(`Entry: ${parts.join(' · ')}.`);
  }
  if (topDrop) {
    lines.push(`Biggest drop-off: ${topDrop.step} (${formatNumber(topDrop.dropOffs)} people).`);
  }
  if (topError) {
    lines.push(`Most common problem: ${prettyError(topError.error)} (${formatNumber(topError.count)} times).`);
  }

  let action = 'Keep watching daily. Focus on the step where most people leave.';
  if (topError && /payment|card/i.test(String(topError.error || ''))) {
    action = 'Check payment failures first — many people are stuck at checkout.';
  } else if (topDrop && /payment/i.test(String(topDrop.step || ''))) {
    action = 'Simplify payment or add clearer help at checkout.';
  } else if (topDrop && /entry|basic/i.test(String(topDrop.step || ''))) {
    action = 'Review the first booking screen — people may not understand what to do next.';
  }

  return { tone, lines, action };
}

export const JOURNEY_QUERY_KEYS = [
  'from', 'to', 'status', 'step', 'deviceType', 'source', 'booked', 'dateBasis', 'stopReason', 'entrySurface',
  'q', 'firstName', 'lastName', 'email', 'phone', 'callStatus'
];

export const CALL_STATUS_FILTER_OPTIONS = [
  { value: 'has_phone', label: 'Has phone number' },
  { value: 'to_call', label: 'Has phone, not called yet' },
  { value: 'called', label: 'Marked as called' }
];

export function applyJourneyQuery(query, state) {
  const q = query || {};
  if (q.from) state.fromDate = q.from;
  if (q.to) state.toDate = q.to;
  const filters = state.filters || {};
  ['status', 'step', 'deviceType', 'source', 'booked', 'dateBasis', 'stopReason', 'entrySurface', 'q', 'firstName', 'lastName', 'email', 'phone', 'callStatus']
    .forEach((key) => {
      if (q[key] != null && q[key] !== '') {
        filters[key] = q[key];
      }
    });
  state.filters = filters;
  return state;
}

export function journeyQuery(fromDate, toDate, filters = {}, extra = {}) {
  const query = Object.assign({}, extra);
  if (fromDate) query.from = fromDate;
  if (toDate) query.to = toDate;
  Object.keys(filters || {}).forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      query[key] = value;
    }
  });
  return query;
}

export function describeAppliedFilters(fromDate, toDate, filters = {}) {
  const parts = [];
  if (fromDate && toDate) {
    const basis = DATE_BASIS_OPTIONS.find((o) => o.value && o.value === filters.dateBasis);
    parts.push(basis ? `${basis.label.toLowerCase()} ${fromDate} → ${toDate}` : `${fromDate} → ${toDate}`);
  }
  if (filters.status) parts.push(`status: ${prettyStatus(filters.status)}`);
  if (filters.step) parts.push(`step: ${prettyStep(filters.step)}`);
  if (filters.deviceType) parts.push(`device: ${filters.deviceType}`);
  if (filters.source) {
    const opt = SOURCE_FILTER_OPTIONS.find((o) => o.value === filters.source);
    parts.push(`source: ${(opt && opt.label) || filters.source}`);
  }
  if (filters.booked === 'yes') parts.push('booked only');
  if (filters.booked === 'completed') parts.push('consult completed only');
  if (filters.booked === 'pending') parts.push('booked, consult not done yet');
  if (filters.booked === 'no') parts.push('not booked');
  if (filters.stopReason) parts.push(`why: ${prettyStopReason(filters.stopReason).toLowerCase()}`);
  if (filters.entrySurface) parts.push(`entry: ${prettyEntrySurface(filters.entrySurface)}`);
  if (filters.firstName) parts.push(`first: ${filters.firstName}`);
  if (filters.lastName) parts.push(`last: ${filters.lastName}`);
  if (filters.email) parts.push(`email: ${filters.email}`);
  if (filters.phone) parts.push(`phone: ${filters.phone}`);
  if (filters.callStatus) {
    const opt = CALL_STATUS_FILTER_OPTIONS.find((o) => o.value === filters.callStatus);
    parts.push(((opt && opt.label) || filters.callStatus).toLowerCase());
  }
  if (filters.q) parts.push(`search: ${filters.q}`);
  return parts;
}
